import React, { useRef } from 'react';
import { ArrowUpDown, Filter, Search, X } from 'lucide-react';

import { Button } from './ui/button';
import { Input } from './ui/input';
import { Tooltip } from './ui/tooltip';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuLabel,
  DropdownMenuRadioGroup,
  DropdownMenuRadioItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger
} from './ui/dropdown-menu';
import { cn } from '../lib/utils';

/**
 * The search, sort and filter row at the top of a dashboard list — Quotes and
 * Phonebook both render it.
 *
 * The search narrows what is already loaded as you type: there is no Search
 * button and nothing is fetched. Sort and filter are ghost icon buttons with a
 * menu each, the same controls the quote request Timeline uses, so a list
 * reads as the same kind of thing wherever it appears.
 *
 * sortOptions:  [{ value, label }]
 * filterGroups: [{ id, label, options: [{ value, label }], value, defaultValue }]
 *
 * The filter button carries a dot while any group is away from its default, so
 * a filtered list never passes for the whole one.
 */
export default function ListToolbar({
  search,
  onSearchChange,
  placeholder = 'Search…',
  sortOptions = [],
  sort,
  onSortChange,
  filterGroups = [],
  onFilterChange,
  className
}) {
  const inputRef = useRef(null);
  const filtered = filterGroups.some((group) => group.value !== group.defaultValue);

  const clearSearch = () => {
    onSearchChange('');
    inputRef.current?.focus();
  };

  return (
    <div className={cn('flex items-center gap-1', className)}>
      <div className="relative min-w-0 flex-1">
        <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
        <Input
          ref={inputRef}
          value={search}
          onChange={(e) => onSearchChange(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === 'Escape' && search) {
              e.preventDefault();
              onSearchChange('');
            }
          }}
          placeholder={placeholder}
          aria-label={placeholder}
          className="h-9 pl-9 pr-9"
        />
        {search && (
          // mousedown is cancelled so the input keeps focus and you can type
          // the next search straight away (see lessons-learned, "Input Control
          // Focus Management"); the click still clears, so Enter works too.
          <Button
            type="button"
            variant="ghost"
            size="icon"
            aria-label="Clear search"
            className="absolute right-0.5 top-1/2 h-8 w-8 -translate-y-1/2"
            onMouseDown={(e) => e.preventDefault()}
            onClick={clearSearch}
          >
            <X />
          </Button>
        )}
      </div>

      {sortOptions.length > 0 && (
        <DropdownMenu>
          <Tooltip content="Sort">
            <DropdownMenuTrigger asChild>
              <Button variant="ghost" size="icon" className="h-9 w-9 shrink-0" aria-label="Sort">
                <ArrowUpDown />
              </Button>
            </DropdownMenuTrigger>
          </Tooltip>
          <DropdownMenuContent align="end">
            <DropdownMenuLabel>Sort by</DropdownMenuLabel>
            <DropdownMenuRadioGroup value={sort} onValueChange={onSortChange}>
              {sortOptions.map((option) => (
                <DropdownMenuRadioItem key={option.value} value={option.value}>
                  {option.label}
                </DropdownMenuRadioItem>
              ))}
            </DropdownMenuRadioGroup>
          </DropdownMenuContent>
        </DropdownMenu>
      )}

      {filterGroups.length > 0 && (
        <DropdownMenu>
          <Tooltip content="Filter">
            <DropdownMenuTrigger asChild>
              <Button
                variant="ghost"
                size="icon"
                className={cn('relative h-9 w-9 shrink-0', filtered && 'text-primary')}
                aria-label={filtered ? 'Filter (active)' : 'Filter'}
              >
                <Filter />
                {filtered && <span className="absolute right-1.5 top-1.5 h-1.5 w-1.5 rounded-full bg-primary" />}
              </Button>
            </DropdownMenuTrigger>
          </Tooltip>
          <DropdownMenuContent align="end" className="min-w-44">
            {filterGroups.map((group, index) => (
              <React.Fragment key={group.id}>
                {index > 0 && <DropdownMenuSeparator />}
                <DropdownMenuLabel>{group.label}</DropdownMenuLabel>
                <DropdownMenuRadioGroup
                  value={group.value}
                  onValueChange={(value) => onFilterChange?.(group.id, value)}
                >
                  {group.options.map((option) => (
                    <DropdownMenuRadioItem key={option.value} value={option.value}>
                      {option.label}
                    </DropdownMenuRadioItem>
                  ))}
                </DropdownMenuRadioGroup>
              </React.Fragment>
            ))}
          </DropdownMenuContent>
        </DropdownMenu>
      )}
    </div>
  );
}
